import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { DeltaPill } from '../components/primitives/DeltaPill';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { HomeTileDelta, formatKpiValue } from './homeDeltas';
import { Panel } from '../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HomeKpiTileProps {
  label: string;
  /** The service colour of the dot. Left out, the brand violet. */
  dotColor?: string;
  /** Null when the reading failed: "—" and "Not read", never a zero (design b3). */
  value: number | null;
  /** Overrides the number text, for a rate such as "33.2%". */
  valueText?: string;
  delta: HomeTileDelta;
  source?: string;
  /** Opens the detail screen of the number. */
  onOpen?: () => void;
}

/** One KPI tile of the Audience or the Streams and smartlinks row (design a1). */
const HomeKpiTileComponent: React.FC<HomeKpiTileProps> = ({
  label,
  dotColor,
  value,
  valueText,
  delta,
  source,
  onOpen,
}) => {
  const intl = useIntl();
  const isNotRead = value === null;

  const body = (
    <>
      <span className={styles.kpiTop}>
        <span className={styles.dot} style={dotColor !== undefined ? { background: dotColor } : undefined} />
        {label}
      </span>
      <span className={clsx(styles.kpiValue, isNotRead && styles.kpiValueFaint)}>
        {isNotRead ? '—' : (valueText ?? formatKpiValue(intl, value))}
      </span>
      <span className={styles.kpiRow}>
        {isNotRead && (
          <span className={clsx(styles.chip, styles.chipWarn)}>{intl.formatMessage(messages.kpiNotRead)}</span>
        )}
        {!isNotRead && delta.kind === 'delta' && <DeltaPill value={delta.value} label={delta.label} />}
        {!isNotRead && delta.kind === 'firstWeek' && (
          <DeltaPill value={0} label={intl.formatMessage(messages.kpiFirstWeek)} />
        )}
        {source !== undefined && <span className={styles.kpiSource}>{source}</span>}
      </span>
    </>
  );

  if (onOpen === undefined) {
    return <Panel className={styles.kpi}>{body}</Panel>;
  }
  return (
    <Panel className={clsx(styles.kpiPanel, styles.kpiPanelButton)}>
      <button type="button" className={clsx(styles.kpi, styles.kpiButton)} onClick={onOpen}>
        {body}
      </button>
    </Panel>
  );
};

export const HomeKpiTile = TypedMemo(HomeKpiTileComponent);
